import React, { useEffect, useState } from "react";
import { Accordion, Button, Form } from "react-bootstrap";
import { availableWorkflows, enumExecutionStatus } from "../../services/availableWorkflows";
import { RecordTypeEnum, VideoRecord } from "../../types";
import {
    getExecutionsForWorkflowType,
    hasExecutionWithStatuses,
    LATEST_EXECUTION_FILTER_ID,
    normalizeExecutionStatus,
} from "../../utils/executionStatuses";

interface RecordFilterProps {
    records: VideoRecord[];
    onFilteredRecordsChange: (records: VideoRecord[]) => void;
}

const enumExecutionStatusToNiceString: { [key: string]: string } = {
    "any": "Any status",
    "none": "No execution",
    "started": "Started",
    "error": "Error",
    "canceled": "Cancelled",
    "completed": "Completed"
}


const RecordFilter: React.FC<RecordFilterProps> = ({
    records,
    onFilteredRecordsChange
}) => {

    const [filteredRecords, setFilteredRecords] = useState<VideoRecord[]>([]);
    const [creatorFilter, setCreatorFilter] = useState<string>("");
    const [recordNameFilter, setRecordNameFilter] = useState<string>("");
    const [videoNameFilter, setVideoNameFilter] = useState<string>("");
    const [videoUploadedFilter, setVideoUploadedFilter] = useState<string>("");
    const [recordType, setRecordType] = useState<string | null>(null);
    const [selectedWorkflowTypes, setSelectedWorkflowTypes] = useState<string[]>([]);
    const [selectedExecutionStatuses, setSelectedExecutionStatuses] = useState<string[]>([]);
    const [subStatusFilter, setSubStatusFilter] = useState<string>("");
    const [excludeSubStatus, setExcludeSubStatus] = useState<boolean>(false);
    const [usesAutoLatestWorkflowType, setUsesAutoLatestWorkflowType] = useState<boolean>(false);


    // Helper functions to calculate default dates
    const getDefaultEndDate = (): string => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    };

    const getDefaultStartDate = (): string => {
        const today = new Date();
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 6);
        return oneMonthAgo.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    };

    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    const handleClearFilters = () => {
        setCreatorFilter("");
        setRecordNameFilter("");
        setVideoNameFilter("");
        setVideoUploadedFilter("");
        setRecordType(null);
        setStartDate(getDefaultStartDate());
        setEndDate(getDefaultEndDate());

        setSelectedWorkflowTypes([]);
        setSelectedExecutionStatuses(["any"]);
        setSubStatusFilter("");
        setExcludeSubStatus(false);
        setUsesAutoLatestWorkflowType(false);
    };
    // Set default dates only on initial mount
    useEffect(() => {
        handleClearFilters()
    }, []);

    // Log when records change
    useEffect(() => {
        if (!records.length) {
            setFilteredRecords([])
            return;
        }

        const creatorSearch = creatorFilter.trim().toLowerCase();
        const recordNameSearch = recordNameFilter.trim().toLowerCase();
        const videoNameSearch = videoNameFilter.trim().toLowerCase();
        const subStatusSearch = subStatusFilter.trim().toLowerCase();

        const recordsWithVideo = records.filter((record: VideoRecord) => { return (record.videos.length > 0); })
        const recordsWithVideoFiltered = recordsWithVideo.filter((record: VideoRecord) => {
            // Filter by creator email / username
            if (creatorSearch) {
                const creatorEmail = (record.user_email || "").toLowerCase();
                const creatorUsername = creatorEmail.includes("@")
                    ? creatorEmail.split("@")[0]
                    : creatorEmail;
                if (!creatorEmail.includes(creatorSearch) && !creatorUsername.includes(creatorSearch)) {
                    return false;
                }
            }

            // Filter by record name
            if (recordNameSearch) {
                const recordName = (record.name || "").toLowerCase();
                if (!recordName.includes(recordNameSearch)) {
                    return false;
                }
            }

            // Filter by video name
            if (videoNameSearch) {
                const hasMatchingVideo = record.videos.some((video) =>
                    (video.title || "").toLowerCase().includes(videoNameSearch)
                );
                if (!hasMatchingVideo) {
                    return false;
                }
            }

            // Filter by video upload status
            if (videoUploadedFilter) {
                const wantUploaded = videoUploadedFilter === "uploaded";
                const hasMatchingVideo = record.videos.some(
                    (video) => Boolean(video.is_uploaded) === wantUploaded
                );
                if (!hasMatchingVideo) {
                    return false;
                }
            }

            // Filter by record type
            if (recordType && record.type !== recordType) {
                return false;
            }

            // Filter by date range (if either start or end date is provided)
            if (startDate || endDate) {
                const recordDate = new Date(record.created_at);
                if (startDate) {
                    const startDateObj = new Date(startDate);
                    if (recordDate < startDateObj) return false;
                }

                // Check end date if provided
                if (endDate) {
                    const endDateObj = new Date(endDate);
                    // Set end date to end of day
                    endDateObj.setHours(23, 59, 59, 999);
                    if (recordDate > endDateObj) return false;
                }
            }

            const hasWorkflowTypeFilter = selectedWorkflowTypes.length > 0;
            const hasExplicitStatusFilter =
                !selectedExecutionStatuses.includes("any") && selectedExecutionStatuses.length > 0;

            if (!hasExplicitStatusFilter && hasWorkflowTypeFilter) {
                const matchesSelectedType = selectedWorkflowTypes.some((workflowType) =>
                    getExecutionsForWorkflowType(record, workflowType).length > 0
                );
                if (!matchesSelectedType) {
                    return false;
                }
            }

            // Filter by workflow type + execution status.
            // If no workflow type is selected, search across all executions.
            if (hasExplicitStatusFilter) {
                if (!hasWorkflowTypeFilter) {
                    const hasMatchingExecution = record.executions.some((execution) =>
                        selectedExecutionStatuses.includes(normalizeExecutionStatus(execution.status))
                    );
                    const matchesNone = selectedExecutionStatuses.includes("none") && record.executions.length === 0;
                    if (!hasMatchingExecution && !matchesNone) {
                        return false;
                    }
                } else {
                    const matchesSelectedType = selectedWorkflowTypes.some((workflowType) =>
                        hasExecutionWithStatuses(record, selectedExecutionStatuses, workflowType)
                    );
                    if (!matchesSelectedType) {
                        return false;
                    }
                }
            }

            // Filter by execution sub-status (e.g. error detail code such as "insufficient_trajectory_points")
            if (subStatusSearch) {
                const executionsToSearch = hasWorkflowTypeFilter
                    ? selectedWorkflowTypes.flatMap((workflowType) => getExecutionsForWorkflowType(record, workflowType))
                    : record.executions;
                const hasMatchingSubStatus = executionsToSearch.some((execution) =>
                    (execution.sub_status || "").toLowerCase().includes(subStatusSearch)
                );
                if (excludeSubStatus ? hasMatchingSubStatus : !hasMatchingSubStatus) {
                    return false;
                }
            }

            return true;
        });

        const recordsArray = recordsWithVideoFiltered.sort((a: VideoRecord, b: VideoRecord) => { return new Date(b.created_at).getTime() - new Date(a.created_at).getTime() });

        setFilteredRecords(recordsArray);
    }, [
        records,
        startDate,
        endDate,
        recordType,
        selectedWorkflowTypes,
        selectedExecutionStatuses,
        subStatusFilter,
        excludeSubStatus,
        creatorFilter,
        recordNameFilter,
        videoNameFilter,
        videoUploadedFilter,
    ]);

    useEffect(() => {
        const hasExplicitStatusFilter =
            !selectedExecutionStatuses.includes("any") && selectedExecutionStatuses.length > 0;

        if (hasExplicitStatusFilter && selectedWorkflowTypes.length === 0) {
            setSelectedWorkflowTypes([LATEST_EXECUTION_FILTER_ID]);
            setUsesAutoLatestWorkflowType(true);
            return;
        }

        if (
            !hasExplicitStatusFilter &&
            usesAutoLatestWorkflowType &&
            selectedWorkflowTypes.length === 1 &&
            selectedWorkflowTypes[0] === LATEST_EXECUTION_FILTER_ID
        ) {
            setSelectedWorkflowTypes([]);
            setUsesAutoLatestWorkflowType(false);
        }
    }, [selectedExecutionStatuses, selectedWorkflowTypes, usesAutoLatestWorkflowType]);



    // Notify parent component when filtered records change
    useEffect(() => {
        onFilteredRecordsChange(filteredRecords);
    }, [filteredRecords, onFilteredRecordsChange]);



    return (
        <div className="record-filter bg-light p-3 mb-4 border rounded">
            <h5 className="border-bottom pb-2 mb-3">Record Filters</h5>
            {/* Text Search Filters */}
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Creator Email</Form.Label>
                <Form.Control
                    type="text"
                    value={creatorFilter}
                    onChange={(e) => setCreatorFilter(e.target.value)}
                    placeholder="Filter by creator email or username"
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Record Name</Form.Label>
                <Form.Control
                    type="text"
                    value={recordNameFilter}
                    onChange={(e) => setRecordNameFilter(e.target.value)}
                    placeholder="Filter by record name"
                />
            </Form.Group>

            {/* Video Filters */}
            <div className="mb-3">
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <span className="fw-bold">Video filter</span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Video Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={videoNameFilter}
                                    onChange={(e) => setVideoNameFilter(e.target.value)}
                                    placeholder="Filter by video title"
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Label className="fw-bold">Upload Status</Form.Label>
                                <Form.Select
                                    value={videoUploadedFilter}
                                    onChange={(e) => setVideoUploadedFilter(e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="uploaded">Uploaded</option>
                                    <option value="not_uploaded">Not uploaded</option>
                                </Form.Select>
                            </Form.Group>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>

            {/* Record Type Filter */}
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Record Type</Form.Label>
                <Form.Select
                    value={recordType || ''}
                    onChange={(e) => setRecordType(e.target.value || null)}
                >
                    <option value="">All Types</option>
                    <option value={RecordTypeEnum.PHOTO}>Photo</option>
                    <option value={RecordTypeEnum.PANORAMA}>Panorama</option>
                </Form.Select>
            </Form.Group>

            {/* Date Range Filters */}
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Start Date</Form.Label>
                <Form.Control
                    type="date"
                    value={startDate || ''}
                    onChange={(e) => setStartDate(e.target.value || null)}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">End Date</Form.Label>
                <Form.Control
                    type="date"
                    value={endDate || ''}
                    onChange={(e) => setEndDate(e.target.value || null)}
                />
            </Form.Group>

            {/* Workflow Type Filters */}
            <div className="mb-3">
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <span className="fw-bold">Workflow Type</span>
                        </Accordion.Header>
                        <Accordion.Body>
                            {availableWorkflows.map((workflow) => (
                                <Form.Check
                                    key={workflow.id}
                                    type="checkbox"
                                    id={`workflow-type-${workflow.id}`}
                                    label={workflow.name}
                                    checked={selectedWorkflowTypes.includes(workflow.id)}
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        setUsesAutoLatestWorkflowType(false);
                                        setSelectedWorkflowTypes((prev) => {
                                            if (isChecked) {
                                                return prev.includes(workflow.id) ? prev : [...prev, workflow.id];
                                            }
                                            return prev.filter((id) => id !== workflow.id);
                                        });
                                    }}
                                />
                            ))}
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>

            {/* Workflow Status Filters */}
            <div className="mb-3">
                <Accordion>
                    <Accordion.Item eventKey="1">
                        <Accordion.Header>
                            <span className="fw-bold">Execution Status</span>
                        </Accordion.Header>
                        <Accordion.Body>
                            {Object.entries(enumExecutionStatus).map(([statusLabel]) => (
                                <Form.Check
                                    key={statusLabel}
                                    type="checkbox"
                                    id={`execution-status-${statusLabel}`}
                                    label={enumExecutionStatusToNiceString[statusLabel]}
                                    checked={selectedExecutionStatuses.includes(statusLabel)}
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        setSelectedExecutionStatuses((prev) => {
                                            if (statusLabel === "any") {
                                                return isChecked ? ["any"] : [];
                                            }

                                            let newFilters = prev.filter((status) => status !== "any");
                                            if (isChecked) {
                                                if (!newFilters.includes(statusLabel)) {
                                                    newFilters.push(statusLabel);
                                                }
                                            } else {
                                                newFilters = newFilters.filter((status) => status !== statusLabel);
                                            }

                                            return newFilters.length === 0 ? ["any"] : newFilters;
                                        });
                                    }}
                                />
                            ))}

                            <Form.Group className="mt-3">
                                <Form.Label className="fw-bold">Error sub-status</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={subStatusFilter}
                                    onChange={(e) => setSubStatusFilter(e.target.value)}
                                    placeholder="e.g. insufficient_trajectory_points"
                                />
                                <Form.Check
                                    className="mt-2"
                                    type="checkbox"
                                    id="sub-status-exclude"
                                    label="Exclude matching (select only records without this sub-status)"
                                    checked={excludeSubStatus}
                                    onChange={(e) => setExcludeSubStatus(e.target.checked)}
                                    disabled={!subStatusFilter.trim()}
                                />
                            </Form.Group>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </div>

            {/* Clear Filters Button */}
            <Button
                variant="outline-secondary"
                size="sm"
                className="w-100"
                onClick={handleClearFilters}
            >
                Clear Filters
            </Button>

            <div className="mt-3 text-muted small">
                <p>Showing {filteredRecords.length} of {records.length} records</p>
            </div>

        </div>
    );
};

export default RecordFilter;
